import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Home, Heart, FileText, MessageSquare, TrendingUp, Eye, Mail,
    Calendar, Clock, AlertCircle
} from 'lucide-react';
import { useDashboard } from '../../../hooks/useDashboard';

/**
 * UserDashboard Component
 * Main dashboard overview for users
 */
const UserDashboard = () => {
    const navigate = useNavigate();
    const { dashboard, loading, error } = useDashboard();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="font-display text-xl font-semibold text-soil-900 mb-2">Error Loading Dashboard</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const stats = dashboard?.statistics || {};
    const profile = dashboard?.profile || {};
    const recentActivity = dashboard?.recentActivity || [];

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900">
                        Welcome back, {profile.firstName || 'User'}!
                    </h1>
                    <p className="mt-2 text-soil-600">
                        Here's what's happening with your property search
                    </p>
                </div>

                {/* Statistics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Saved Properties */}
                    <div
                        onClick={() => navigate('/saved-properties')}
                        className="bg-white rounded-lg shadow-sm p-6 border border-soil-100 hover:shadow-md transition-shadow cursor-pointer"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Saved Properties</p>
                                <p className="text-3xl font-bold text-soil-900 mt-2">
                                    {stats.savedProperties || 0}
                                </p>
                            </div>
                            <div className="p-3 bg-murram-100 rounded-lg">
                                <Heart className="h-6 w-6 text-murram-500" />
                            </div>
                        </div>
                    </div>

                    {/* Applications */}
                    <div
                        onClick={() => navigate('/applications')}
                        className="bg-white rounded-lg shadow-sm p-6 border border-soil-100 hover:shadow-md transition-shadow cursor-pointer"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Applications</p>
                                <p className="text-3xl font-bold text-soil-900 mt-2">
                                    {stats.applications?.total || 0}
                                </p>
                                <p className="text-xs text-soil-400 mt-1">
                                    {stats.applications?.pending || 0} pending
                                </p>
                            </div>
                            <div className="p-3 bg-highland-50 rounded-lg">
                                <FileText className="h-6 w-6 text-highland-500" />
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    <div
                        onClick={() => navigate('/messages')}
                        className="bg-white rounded-lg shadow-sm p-6 border border-soil-100 hover:shadow-md transition-shadow cursor-pointer"
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Messages</p>
                                <p className="text-3xl font-bold text-soil-900 mt-2">
                                    {stats.unreadMessages || 0}
                                </p>
                                <p className="text-xs text-soil-400 mt-1">unread</p>
                            </div>
                            <div className="p-3 bg-dawn-100/40 rounded-lg">
                                <MessageSquare className="h-6 w-6 text-dawn-600" />
                            </div>
                        </div>
                    </div>

                    {/* Properties Viewed */}
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-soil-600">Properties Viewed</p>
                                <p className="text-3xl font-bold text-soil-900 mt-2">
                                    {stats.propertiesViewed || 0}
                                </p>
                            </div>
                            <div className="p-3 bg-jacaranda-100 rounded-lg">
                                <Eye className="h-6 w-6 text-jacaranda-500" />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Recent Activity */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h2 className="font-display text-xl font-semibold text-soil-900 mb-4">Recent Activity</h2>

                            {recentActivity.length > 0 ? (
                                <div className="space-y-4">
                                    {recentActivity.map((activity, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start space-x-3 p-3 hover:bg-soil-50 rounded-lg transition-colors"
                                        >
                                            <div className="flex-shrink-0 p-2 bg-highland-50 rounded-lg">
                                                {activity.type === 'property_view' && <Eye className="h-4 w-4 text-highland-500" />}
                                                {activity.type === 'application' && <FileText className="h-4 w-4 text-highland-500" />}
                                                {activity.type === 'message' && <MessageSquare className="h-4 w-4 text-highland-500" />}
                                                {activity.type === 'saved' && <Heart className="h-4 w-4 text-highland-500" />}
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-soil-900">
                                                    {activity.propertyName || 'Activity'}
                                                </p>
                                                <p className="text-xs text-soil-400 mt-1">
                                                    {new Date(activity.date).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-soil-400">
                                    <Clock className="h-12 w-12 mx-auto mb-3 text-soil-400" />
                                    <p className="text-sm">No recent activity</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="space-y-6">
                        {/* Account Info */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h3 className="text-lg font-semibold text-soil-900 mb-4">Account</h3>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-soil-400">Email</p>
                                    <p className="text-sm font-medium text-soil-900">{profile.email}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-soil-400">Member Since</p>
                                    <p className="text-sm font-medium text-soil-900">
                                        {profile.memberSince
                                            ? new Date(profile.memberSince).toLocaleDateString('en-US', {
                                                month: 'long',
                                                year: 'numeric'
                                            })
                                            : 'N/A'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => navigate('/profile')}
                                    className="w-full mt-4 px-4 py-2 bg-soil-100 text-soil-600 rounded-lg hover:bg-soil-100 transition-colors text-sm font-medium"
                                >
                                    View Profile
                                </button>
                            </div>
                        </div>

                        {/* Quick Links */}
                        <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                            <h3 className="text-lg font-semibold text-soil-900 mb-4">Quick Links</h3>
                            <div className="space-y-2">
                                <button
                                    onClick={() => navigate('/listings')}
                                    className="w-full flex items-center px-4 py-3 text-left text-sm text-soil-600 hover:bg-soil-50 rounded-lg transition-colors"
                                >
                                    <Home className="h-4 w-4 mr-3 text-soil-400" />
                                    Browse Properties
                                </button>
                                <button
                                    onClick={() => navigate('/saved-properties')}
                                    className="w-full flex items-center px-4 py-3 text-left text-sm text-soil-600 hover:bg-soil-50 rounded-lg transition-colors"
                                >
                                    <Heart className="h-4 w-4 mr-3 text-soil-400" />
                                    Saved Properties
                                </button>
                                <button
                                    onClick={() => navigate('/applications')}
                                    className="w-full flex items-center px-4 py-3 text-left text-sm text-soil-600 hover:bg-soil-50 rounded-lg transition-colors"
                                >
                                    <FileText className="h-4 w-4 mr-3 text-soil-400" />
                                    My Applications
                                </button>
                                <button
                                    onClick={() => navigate('/settings')}
                                    className="w-full flex items-center px-4 py-3 text-left text-sm text-soil-600 hover:bg-soil-50 rounded-lg transition-colors"
                                >
                                    <TrendingUp className="h-4 w-4 mr-3 text-soil-400" />
                                    Settings
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDashboard;
